import { getKanaForName } from './nameKanaMap';

// 週報一覧で「最低限ここだけあれば比較できる」という想定のゆるい型
export type WeeklyLike = {
  wr_id?: number | string;
  WR_ID?: number | string;

  department_id?: number | string | null;
  DEPARTMENT_ID?: number | string | null;

  team_id?: number | string | null;
  TEAM_ID?: number | string | null;

  report_type_id?: number | string | null;
  REPORT_TYPE_ID?: number | string | null;

  start_date?: string | null; // '2025-02-01' 形式想定
  START_DATE?: string | null;

  end_date?: string | null;
  END_DATE?: string | null;

  // ===== データのキーに合わせる =====
  // API が department / team というキーで返してくる場合にも対応
  department?: number | string | null;
  team?: number | string | null;

  // -------- 人の名前（五十音順ソート用） --------
  reporter_name?: string | null;
  REPORTER_NAME?: string | null;
  reporter?: string | null;
  REPORTER?: string | null;
  create_user_name?: string | null;
  CREATE_USER_NAME?: string | null;
  create_by_name?: string | null;
  CREATE_BY_NAME?: string | null;

  // かな（ふりがな）が返ってくる場合はこちらに入れる想定
  // ※ なくても動くようにすべて optional にしておく
  reporter_kana?: string | null;
  REPORTER_KANA?: string | null;
  create_user_kana?: string | null;
  CREATE_USER_KANA?: string | null;
  create_by_kana?: string | null;
  CREATE_BY_KANA?: string | null;

  // API が持っている create_by 系（最終手段）
  create_by?: string | null;
  CREATE_BY?: string | null;

  // API が持っているユーザーID系（メールアドレス想定）
  create_by_user_id?: string | null;
  CREATE_BY_USER_ID?: string | null;

  // -------- レコード作成日時 --------
  created_at?: string | null; // '2025-02-01T12:34:56' など
  CREATED_AT?: string | null;
  create_dt?: string | null;
  CREATE_DT?: string | null;
};

// チームの優先順位マップ
//   key: team_id (string 化したもの)
//   value: 並び順インデックス（0, 1, 2, ... 小さいほど上にくる）
export type TeamSortIndexMap = Record<string, number>;

// 課の優先順位マップ
export type DeptSortIndexMap = Record<string, number>;

// ユーザーの優先順位マップ
//   key: create_by_user_id（メールアドレスなど論理ID）
//   value: USER_INFO_MANAGE の取得順を表すインデックス（小さいほど上）
export type UserSortIndexMap = Record<string, number>;

// 「どのキーを優先してソートするか」を表すモード
// いまは DATE/NAME 系だけ用意しておき、将来増やしやすいようにしておく
export type SortMode =
  | 'DEFAULT' // 開始日付 → 課 → チーム → 人 → 作成日時
  | 'DATE_ASC' // 日付昇順（DEFAULT と同じ／newestFirst=false）
  | 'DATE_DESC' // 日付降順（newestFirst=true）
  | 'NAME_ASC' // 人名昇順 → 日付 → 課 → チーム → 作成日時 → 週報分類 → wr_id
  | 'NAME_DESC' // 人名降順 → 日付 → 課 → チーム → 作成日時 → 週報分類 → wr_id
  | 'ATTR_ASC' // カスタム属性値（昇順）→ 日付 → 課 → チーム → 作成日時
  | 'ATTR_DESC'; // カスタム属性値（降順）→ 日付 → 課 → チーム → 作成日時

export type WeeklySortOptions = {
  teamSortIndexMap?: TeamSortIndexMap;
  deptSortIndexMap?: DeptSortIndexMap;
  // true の場合は「新しい日付が上」＝日付降順
  newestFirst?: boolean;
  // どのソートモードを使うか
  sortMode?: SortMode;
  // USER_INFO_MANAGE の取得順を保持するマップ
  //   key: create_by_user_id（メールアドレスなど）
  //   value: 0,1,2,...（小さいほど優先）
  userSortIndexMap?: UserSortIndexMap;
  // カスタム属性ソート用設定
  attrSortConfig?: {
    attrId: number;
    optionOrderMap: Record<string, number>;
  };
};

// ========== 共通の小さいユーティリティ ==========

// null / undefined セーフな日付比較（YYYY-MM-DD or ISO 文字列想定）
const compareDate = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  const da = new Date(a);
  const db = new Date(b);
  const ta = da.getTime();
  const tb = db.getTime();

  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;

  if (ta === tb) return 0;
  // 昇順（古い日付が先）
  return ta < tb ? -1 : 1;
};

// 作成日時用: null / undefined セーフな日時比較
const compareDateTime = (
  a: string | null | undefined,
  b: string | null | undefined,
): number => {
  if (!a && !b) return 0;
  if (!a) return 1;
  if (!b) return -1;

  const da = new Date(a);
  const db = new Date(b);
  const ta = da.getTime();
  const tb = db.getTime();

  if (Number.isNaN(ta) && Number.isNaN(tb)) return 0;
  if (Number.isNaN(ta)) return 1;
  if (Number.isNaN(tb)) return -1;

  if (ta === tb) return 0;
  // 昇順（古い日時が先）
  return ta < tb ? -1 : 1;
};

// 人名の取り出し（候補をいくつか見る）
const resolveName = (w: WeeklyLike): string | null => {
  const cand =
    w.REPORTER_NAME ??
    w.reporter_name ??
    w.REPORTER ??
    w.reporter ??
    w.CREATE_USER_NAME ??
    w.create_user_name ??
    w.CREATE_BY_NAME ??
    w.create_by_name ??
    w.CREATE_BY ??
    w.create_by ??
    null;

  if (cand == null) return null;
  const s = String(cand).trim();
  return s.length ? s : null;
};

// 人名かな（ふりがな）の取り出し（あればこちらを優先して五十音ソートに使う）
const resolveNameKana = (w: WeeklyLike): string | null => {
  const cand =
    w.REPORTER_KANA ??
    w.reporter_kana ??
    w.CREATE_USER_KANA ??
    w.create_user_kana ??
    w.CREATE_BY_KANA ??
    w.create_by_kana ??
    null;

  if (cand == null) return null;
  const s = String(cand).trim();
  return s.length ? s : null;
};

// create_by_user_id（メールアドレスなど論理ID）の取り出し
const resolveCreateByUserId = (w: WeeklyLike): string | null => {
  const cand =
    w.CREATE_BY_USER_ID ??
    w.create_by_user_id ??
    null;

  if (cand == null) return null;
  const s = String(cand).trim();
  return s.length ? s : null;
};

// 作成日時の取り出し（候補をいくつか見る）
const resolveCreatedAt = (w: WeeklyLike): string | null => {
  const cand =
    w.CREATED_AT ??
    w.created_at ??
    w.CREATE_DT ??
    w.create_dt ??
    null;

  if (cand == null) return null;
  const s = String(cand).trim();
  return s.length ? s : null;
};

// 複数の比較関数を順番に適用していくヘルパー
const chainComparators =
  (
    comparators: Array<(a: WeeklyLike, b: WeeklyLike) => number>,
  ) =>
  (a: WeeklyLike, b: WeeklyLike): number => {
    for (const compare of comparators) {
      const result = compare(a, b);
      if (result !== 0) {
        return result;
      }
    }
    return 0;
  };

// ========== 各キーごとの小さい比較関数（クロージャ） ==========

// 開始日付（なければ終了日付）で比較
const createDateComparator = (
  opts?: WeeklySortOptions,
): ((a: WeeklyLike, b: WeeklyLike) => number) => {
  const newestFirst = opts?.newestFirst ?? false;

  return (a, b) => {
    const startA = a.START_DATE ?? a.start_date ?? null;
    const startB = b.START_DATE ?? b.start_date ?? null;
    const endA = a.END_DATE ?? a.end_date ?? null;
    const endB = b.END_DATE ?? b.end_date ?? null;

    const baseCmp = compareDate(
      startA || endA || undefined,
      startB || endB || undefined,
    );
    if (baseCmp === 0) {
      return 0;
    }

    // newestFirst=true のときは符号反転させて降順にする
    return newestFirst ? -baseCmp : baseCmp;
  };
};

// 課IDで比較（マスタの並び順を優先）
const createDeptComparator = (
  opts?: WeeklySortOptions,
): ((a: WeeklyLike, b: WeeklyLike) => number) => {
  const deptSortIndexMap = opts?.deptSortIndexMap;

  return (a, b) => {
    // department も見る
    const deptA =
      a.DEPARTMENT_ID ??
      a.department_id ??
      a.department ??
      null;
    const deptB =
      b.DEPARTMENT_ID ??
      b.department_id ??
      b.department ??
      null;

    if (deptSortIndexMap && deptA != null && deptB != null) {
      const ka = deptSortIndexMap[String(deptA)] ?? Number.MAX_SAFE_INTEGER;
      const kb = deptSortIndexMap[String(deptB)] ?? Number.MAX_SAFE_INTEGER;
      if (ka !== kb) {
        return ka - kb;
      }
    } else if (deptA != null && deptB != null && deptA !== deptB) {
      // マップがなければ単純に昇順
      const sA = String(deptA);
      const sB = String(deptB);
      if (sA !== sB) {
        return sA.localeCompare(sB, 'ja-JP');
      }
    }

    return 0;
  };
};

// チームIDで比較（マスタの並び順を優先）
//   同じ課内では team_id の数値昇順になるように TeamSortIndexMap を作っておき、
//   ここではそのインデックスを比較するだけにする。
const createTeamComparator = (
  opts?: WeeklySortOptions,
): ((a: WeeklyLike, b: WeeklyLike) => number) => {
  const teamSortIndexMap = opts?.teamSortIndexMap;

  return (a, b) => {
    const teamA =
      a.TEAM_ID ??
      a.team_id ??
      a.team ??
      null;
    const teamB =
      b.TEAM_ID ??
      b.team_id ??
      b.team ??
      null;

    if (teamSortIndexMap && teamA != null && teamB != null) {
      const ia = teamSortIndexMap[String(teamA)] ?? Number.MAX_SAFE_INTEGER;
      const ib = teamSortIndexMap[String(teamB)] ?? Number.MAX_SAFE_INTEGER;
      if (ia !== ib) {
        return ia - ib;
      }
    } else if (teamA != null && teamB != null && teamA !== teamB) {
      const sA = String(teamA);
      const sB = String(teamB);
      if (sA !== sB) {
        return sA.localeCompare(sB, 'ja');
      }
    }

    return 0;
  };
};

// 人名（五十音）の比較
const createNameComparator = (
  direction: 'asc' | 'desc' = 'asc',
  opts?: WeeklySortOptions,
): ((a: WeeklyLike, b: WeeklyLike) => number) => {
  // 日本語用の Collator を一度だけ生成
  const collator = new Intl.Collator('ja', {
    usage: 'sort',
    sensitivity: 'base',
    numeric: false,
  });

  const userSortIndexMap = opts?.userSortIndexMap;

  return (a, b) => {
    // かながあれば最優先で使う
    const kanaA = resolveNameKana(a);
    const kanaB = resolveNameKana(b);

    const keyA = kanaA ?? resolveName(a);
    const keyB = kanaB ?? resolveName(b);

    if (keyA && keyB) {
      if (keyA !== keyB) {
        const base = collator.compare(keyA, keyB);
        return direction === 'asc' ? base : -base;
      }

      // 「姓・名・フリガナ」が完全一致する場合:
      // USER_INFO_MANAGE の取得順（userSortIndexMap）でタイブレーク
      if (userSortIndexMap) {
        const userIdA = resolveCreateByUserId(a);
        const userIdB = resolveCreateByUserId(b);

        if (userIdA && userIdB && userIdA !== userIdB) {
          const indexA =
            userSortIndexMap[userIdA] ?? Number.MAX_SAFE_INTEGER;
          const indexB =
            userSortIndexMap[userIdB] ?? Number.MAX_SAFE_INTEGER;

          if (indexA !== indexB) {
            // index が小さいほうを先に
            return indexA - indexB;
          }
        }
      }
    }

    // 片方だけ null / 空文字 の場合は「同じ」とみなして後続キーに任せる
    return 0;
  };
};

// 作成日時の比較
const createCreatedAtComparator = (): ((
  a: WeeklyLike,
  b: WeeklyLike,
) => number) => {
  return (a, b) => {
    const createdA = resolveCreatedAt(a);
    const createdB = resolveCreatedAt(b);
    return compareDateTime(
      createdA || undefined,
      createdB || undefined,
    );
  };
};

// 週報分類（report_type_id）の比較
const createReportTypeComparator = (): ((
  a: WeeklyLike,
  b: WeeklyLike,
) => number) => {
  return (a, b) => {
    const rtA = a.REPORT_TYPE_ID ?? a.report_type_id ?? null;
    const rtB = b.REPORT_TYPE_ID ?? b.report_type_id ?? null;

    if (rtA != null && rtB != null && rtA !== rtB) {
      const sA = String(rtA);
      const sB = String(rtB);
      if (sA !== sB) {
        return sA.localeCompare(sB, 'ja');
      }
    }

    return 0;
  };
};

// wr_id で比較（最後の安定ソート用）
const createWrIdComparator = (): ((
  a: WeeklyLike,
  b: WeeklyLike,
) => number) => {
  return (a, b) => {
    const idA = a.WR_ID ?? a.wr_id ?? null;
    const idB = b.WR_ID ?? b.wr_id ?? null;

    if (idA != null && idB != null && idA !== idB) {
      const nA = Number(idA);
      const nB = Number(idB);
      if (!Number.isNaN(nA) && !Number.isNaN(nB)) {
        return nA - nB;
      }

      const sA = String(idA);
      const sB = String(idB);
      if (sA !== sB) {
        return sA.localeCompare(sB, 'ja');
      }
    }

    return 0;
  };
};

// カスタム属性値の比較
// report.attributes 配列から attrId に一致する attr_value を取り出し、
// optionOrderMap のインデックスで比較する（値なしは末尾）
const createAttrComparator = (
  config: { attrId: number; optionOrderMap: Record<string, number> },
  direction: 'asc' | 'desc' = 'asc',
): ((a: WeeklyLike, b: WeeklyLike) => number) => {
  return (a, b) => {
    const getVal = (row: WeeklyLike): string | null => {
      const attrs = Array.isArray((row as any)?.attributes)
        ? (row as any).attributes
        : [];
      const found = attrs.find(
        (x: any) => Number((x as any)?.attr_id) === config.attrId,
      );
      if (!found) return null;
      const v = (found as any)?.attr_value ?? null;
      return v != null ? String(v).trim() : null;
    };

    const valA = getVal(a);
    const valB = getVal(b);

    if (valA === null && valB === null) return 0;
    if (valA === null) return 1;
    if (valB === null) return -1;

    const idxA = config.optionOrderMap[valA] ?? Number.MAX_SAFE_INTEGER;
    const idxB = config.optionOrderMap[valB] ?? Number.MAX_SAFE_INTEGER;

    if (idxA === idxB) return 0;
    const cmp = idxA - idxB;
    return direction === 'asc' ? cmp : -cmp;
  };
};

// ========== モード別のコンパレータ構築 ==========

// 既存の仕様:
// 開始日付（昇順/降順）→ 課 → チーム → 人 → 作成日時 → 週報分類 → wr_id
const buildDefaultComparator = (
  opts?: WeeklySortOptions,
): ((a: WeeklyLike, b: WeeklyLike) => number) => {
  const cmpDate = createDateComparator(opts);
  const cmpDept = createDeptComparator(opts);
  const cmpTeam = createTeamComparator(opts);
  const cmpNameAsc = createNameComparator('asc', opts);
  const cmpCreatedAt = createCreatedAtComparator();
  const cmpReportType = createReportTypeComparator();
  const cmpWrId = createWrIdComparator();

  return chainComparators([
    cmpDate,
    cmpDept,
    cmpTeam,
    cmpNameAsc,
    cmpCreatedAt,
    cmpReportType,
    cmpWrId,
  ]);
};

// 「人名を一番優先にする」モード
const buildNameFirstComparator = (
  direction: 'asc' | 'desc',
  opts?: WeeklySortOptions,
): ((a: WeeklyLike, b: WeeklyLike) => number) => {
  const cmpName = createNameComparator(direction, opts);
  const cmpDate = createDateComparator(opts);
  const cmpDept = createDeptComparator(opts);
  const cmpTeam = createTeamComparator(opts);
  const cmpCreatedAt = createCreatedAtComparator();
  const cmpReportType = createReportTypeComparator();
  const cmpWrId = createWrIdComparator();

  return chainComparators([
    // まず人名
    cmpName,
    // 同じ人の中での並びはこれらで決める
    cmpDate,
    cmpDept,
    cmpTeam,
    cmpCreatedAt,
    cmpReportType,
    cmpWrId,
  ]);
};

// SortMode から実際の比較関数を作る
export const buildComparatorBySortMode = (
  mode: SortMode,
  opts?: WeeklySortOptions,
): ((a: WeeklyLike, b: WeeklyLike) => number) => {
  switch (mode) {
    case 'DATE_ASC':
      // newestFirst=false を強制
      return buildDefaultComparator({
        ...opts,
        newestFirst: false,
      });

    case 'DATE_DESC':
      // newestFirst=true を強制
      return buildDefaultComparator({
        ...opts,
        newestFirst: true,
      });

    case 'NAME_ASC':
      return buildNameFirstComparator('asc', opts);

    case 'NAME_DESC':
      return buildNameFirstComparator('desc', opts);

    case 'ATTR_ASC':
    case 'ATTR_DESC': {
      const config = opts?.attrSortConfig;
      if (!config) {
        return buildDefaultComparator(opts);
      }
      const dir = mode === 'ATTR_ASC' ? 'asc' : 'desc';
      const cmpAttr = createAttrComparator(config, dir);
      const cmpDate = createDateComparator(opts);
      const cmpDept = createDeptComparator(opts);
      const cmpTeam = createTeamComparator(opts);
      const cmpCreatedAt = createCreatedAtComparator();
      const cmpWrId = createWrIdComparator();
      return chainComparators([cmpAttr, cmpDate, cmpDept, cmpTeam, cmpCreatedAt, cmpWrId]);
    }

    case 'DEFAULT':
    default:
      // 既存の挙動：opts.newestFirst で昇順/降順を切り替え
      return buildDefaultComparator(opts);
  }
};

// 呼び出し側からは従来どおり
//   arr.sort(compareWeeklyReports({ ... }))
// の形で使えるようにしておく
export function compareWeeklyReports(
  opts?: WeeklySortOptions,
): (a: WeeklyLike, b: WeeklyLike) => number {
  const mode = opts?.sortMode ?? 'DEFAULT';
  return buildComparatorBySortMode(mode, opts);
}

// チームマスタから teamSortIndexMap を作るユーティリティ
// （週報連 / 入力修正の両方で使える）
//
// ポイント:
//   - 同じ department_id ごとにグルーピング
//   - その中で order_number（マスタの並び順）→ team_id の昇順に並べる
//   - 欠番（1, 2, 5, 8, 12 ... のような歯抜け）はそのまま許容
export const buildTeamSortIndexMap = (
  teams: Array<{
    team_id?: number | string;
    id?: number | string;
    department_id?: number | string;
    department?: number | string;
    order_number?: number | null;
  }>,
): TeamSortIndexMap => {
  const map: TeamSortIndexMap = {};

  const toIntSafe = (value: number | string | undefined | null): number => {
    if (value == null) {
      return Number.MAX_SAFE_INTEGER;
    }
    const n = Number(value);
    return Number.isNaN(n) ? Number.MAX_SAFE_INTEGER : n;
  };

  // department_id → team_id のペアで昇順ソート
  const sorted = [...teams].sort((a, b) => {
    const deptA =
      a.department_id ??
      a.department ??
      null;
    const deptB =
      b.department_id ??
      b.department ??
      null;

    const deptNumA = toIntSafe(deptA);
    const deptNumB = toIntSafe(deptB);

    if (deptNumA !== deptNumB) {
      return deptNumA - deptNumB;
    }

    // マスタの並び順（order_number）を優先。未設定は最後尾
    const orderA = toIntSafe(a.order_number);
    const orderB = toIntSafe(b.order_number);
    if (orderA !== orderB) {
      return orderA - orderB;
    }

    const idA = a.team_id ?? a.id ?? null;
    const idB = b.team_id ?? b.id ?? null;

    const teamNumA = toIntSafe(idA);
    const teamNumB = toIntSafe(idB);

    if (teamNumA !== teamNumB) {
      return teamNumA - teamNumB;
    }

    // 数値として同じかどちらか NaN のときは、念のため文字列比較で安定させる
    const sA = idA != null ? String(idA) : '';
    const sB = idB != null ? String(idB) : '';
    if (sA !== sB) {
      return sA.localeCompare(sB, 'ja');
    }

    return 0;
  });

  sorted.forEach((t, idx) => {
    const key = t.team_id ?? t.id;
    if (key != null) {
      map[String(key)] = idx;
    }
  });

  return map;
};

// 課マスタから deptSortIndexMap を作るユーティリティ
// （週報連 / 入力修正の両方で使える）
// order_number が大きい課ほど上に来るように「降順」で並べます。
export const buildDeptSortIndexMap = (
  departments: Array<{
    department_id?: number | string;
    id?: number | string;
    order_number?: number | null;
  }>,
): DeptSortIndexMap => {
  const map: DeptSortIndexMap = {};

  // order_number 降順（大きいほど上）→ インデックス
  const sorted = [...departments].sort((a, b) => {
    const oa = a.order_number ?? Number.MIN_SAFE_INTEGER;
    const ob = b.order_number ?? Number.MIN_SAFE_INTEGER;
    if (oa === ob) return 0;
    return oa > ob ? -1 : 1;
  });

  sorted.forEach((d, idx) => {
    const key = d.department_id ?? d.id;
    if (key != null) {
      map[String(key)] = idx;
    }
  });

  return map;
};

// ========== フロント側辞書で「かな」を補完するユーティリティ ==========
//
// WeeklyLike がもともと *_KANA フィールドを持っていれば
// それはそのまま createNameComparator で利用する。
// 足りない場合だけ NAME_KANA_MAP から補完する。
//
// さらに今回は、DB から取得した「読み仮名（かな）」があれば
// それを最優先で利用し、DB に無い人だけフロント側辞書で補完する。
export async function attachKanaForWeeklyList<T extends WeeklyLike>(
  list: T[],
  options?: {
    // key: create_by_user_id（メールアドレス）やフルネーム
    // value: "やまだ じろう" 等のかな
    userKanaMap?: Record<string, string>;
  },
): Promise<T[]> {
  const userKanaMap = options?.userKanaMap ?? {};

  const patched = list.map((item) => {
    // すでに kana を持っているレコードはそのまま返す
    if (resolveNameKana(item)) {
      return item;
    }

    // かなが無い場合だけ、名前から辞書や DB 情報を引く
    const name = resolveName(item);
    if (!name) {
      return item;
    }

    const trimmedName = String(name).trim();

    // 1. DB 側のかな: create_by_user_id をキーにして取得（USER_INFO_MANAGE 由来）
    const userId = resolveCreateByUserId(item);
    let kana: string | null | undefined = null;

    if (userId) {
      kana = userKanaMap[userId];
    }

    // 2. create_by_user_id では見つからなかった場合のみ、
    //    従来どおり「フルネーム」をキーにして DB 側かなを探す
    if (!kana && trimmedName) {
      kana = userKanaMap[trimmedName];
    }

    // 3. DB に無い場合だけフロント側辞書から補完する
    if (!kana) {
      kana = getKanaForName(trimmedName);
    }

    if (!kana) {
      // どちらにも無ければ何もせず返す（漢字のままソート）
      return item;
    }

    // create_by 系にかなを付与して返す
    return {
      ...item,
      create_by_kana: kana,
      CREATE_BY_KANA: kana,
    } as T;
  });

  return patched;
}
